/**
 * Google Contacts Service
 * Handles Google OAuth authentication and People API integration
 * Now uses redirect-based OAuth (like Gmail/Calendar) for consistent consent screens
 */

import { getOAuthConfig } from '../utils/oauth/dynamicRedirectUri';

class GoogleContactsService {
  constructor() {
    // Use VITE_GOOGLE_OAUTH_CLIENT_ID to match the rest of the application
    this.CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    // For API key, use VITE_GOOGLE_API_KEY or fallback to VITE_GOOGLE_PLACES_API_KEY
    // The API key is used for initializing the gapi client
    this.API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    this.DISCOVERY_DOC = 'https://people.googleapis.com/$discovery/rest?version=v1';
    // Use consolidated scopes from environment variable for single consent screen
    // Fallback to contacts scope if env var not set (for backward compatibility)
    const envScopes = (import.meta.env.VITE_GOOGLE_OAUTH_SCOPES || '').split(',').filter(scope => scope.trim());
    this.SCOPES = envScopes.length > 0 ? envScopes.join(' ') : 'https://www.googleapis.com/auth/contacts.readonly';
    
    this.gapi = null;
    this.tokenClient = null;
    this.isInitialized = false;
    this._redirectUri = null; // Will be set dynamically
  }

  /**
   * Get the current redirect URI (dynamic or configured)
   * @returns {string} Redirect URI
   */
  get redirectUri() {
    if (!this._redirectUri) {
      // Initialize dynamic redirect URI when first accessed
      const config = getOAuthConfig();
      this._redirectUri = `${config.redirectUri.replace('/auth/google/callback', '')}/auth/google/contacts-callback`;
    }
    return this._redirectUri;
  }

  /**
   * Initialize Google API and OAuth
   */
  async initialize() {
    if (this.isInitialized) return true;

    try {
      // Check if required environment variables are set
      // Note: API_KEY is optional for People API since it uses OAuth tokens
      if (!this.CLIENT_ID) {
        throw new Error('Google OAuth credentials not configured. Please set VITE_GOOGLE_OAUTH_CLIENT_ID in your environment variables.');
      }

      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        throw new Error('Google Contacts Service can only be used in a browser environment.');
      }

      // Load Google API script with timeout
      await Promise.race([
        this.loadGoogleAPI(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout loading Google API')), 10000)
        )
      ]);
      
      // Initialize gapi with timeout
      await Promise.race([
        new Promise((resolve, reject) => {
          window.gapi.load('client', {
            callback: resolve,
            onerror: reject
          });
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout initializing Google API client')), 5000)
        )
      ]);

      // Initialize the API client
      // Note: API key is optional for People API since it requires OAuth tokens
      // The API key restriction may block the discovery document, so we skip it
      // and use direct REST API calls instead
      const initConfig = {};
      
      // Only add API key if provided (it's optional for OAuth-protected APIs)
      if (this.API_KEY) {
        initConfig.apiKey = this.API_KEY;
      }
      
      await window.gapi.client.init(initConfig);

      this.gapi = window.gapi;

      // Initialize Google Identity Services with timeout
      await Promise.race([
        this.loadGoogleIdentityServices(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout loading Google Identity Services')), 10000)
        )
      ]);
      
      // Check if Google Identity Services loaded properly
      if (!window.google?.accounts?.oauth2) {
        throw new Error('Failed to load Google Identity Services - oauth2 not available');
      }
      
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: this.CLIENT_ID,
        scope: this.SCOPES,
        callback: '', // Will be set per request
      });

      this.isInitialized = true;
      return true;

    } catch (error) {
      console.error('Failed to initialize Google Contacts Service:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Load Google API script dynamically
   */
  loadGoogleAPI() {
    return new Promise((resolve, reject) => {
      if (window.gapi) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Load Google Identity Services script
   */
  loadGoogleIdentityServices() {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Generate OAuth2 authorization URL using redirect-based flow (consistent with Gmail/Calendar)
   * @returns {Promise<string>} Authorization URL
   */
  async getAuthUrl() {
    if (!this.CLIENT_ID || !this.redirectUri) {
      throw new Error('Google OAuth credentials not configured. Please set VITE_GOOGLE_OAUTH_CLIENT_ID in your environment variables.');
    }

    const scopeString = this.SCOPES_ARRAY.join(' ');

    // Generate OAuth URL with PKCE (same approach as GmailOAuthService)
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later use
    sessionStorage.setItem('google_contacts_oauth_code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: this.CLIENT_ID,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: scopeString,
      access_type: 'offline',
      include_granted_scopes: 'true',
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    console.log('GoogleContactsService: Generated auth URL with consolidated scopes:', scopeString);
    
    return authUrl;
  }

  /**
   * Generate PKCE code verifier
   */
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate PKCE code challenge
   */
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Authenticate user and get access token (legacy tokenClient method - kept for backward compatibility)
   * @deprecated Use getAuthUrl() and handleOAuthCallback() for redirect-based flow instead
   */
  async authenticate() {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      try {
        // Set callback for this specific request
        this.tokenClient.callback = (response) => {
          if (response.error !== undefined) {
            console.error('Google OAuth error details:', response);
            const errorMsg = response.error?.message || response.error || 'Unknown OAuth error';
            reject(new Error(`Google OAuth error: ${errorMsg}`));
            return;
          }
          // Store the token in gapi client for consistency
          if (this.gapi?.client) {
            this.gapi.client.setToken({ access_token: response.access_token });
          }
          resolve(response.access_token);
        };

        // Always prompt for consent to ensure all scopes are requested
        // This ensures we get all consolidated scopes, not just incremental ones
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Handle OAuth callback from redirect flow
   * @param {string} code - Authorization code from OAuth callback
   * @returns {Promise<string>} Access token
   */
  async handleOAuthCallback(code) {
    try {
      const codeVerifier = sessionStorage.getItem('google_contacts_oauth_code_verifier');
      if (!codeVerifier) {
        throw new Error('OAuth code verifier not found. Please try authenticating again.');
      }

      // Exchange authorization code for tokens
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.CLIENT_ID,
          code: code,
          redirect_uri: this.redirectUri,
          grant_type: 'authorization_code',
          code_verifier: codeVerifier,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        throw new Error(errorData.error_description || `Token exchange failed: ${tokenResponse.statusText}`);
      }

      const tokens = await tokenResponse.json();
      
      // Store token in gapi client for consistency
      if (this.gapi?.client) {
        this.gapi.client.setToken({ access_token: tokens.access_token });
      }

      // Clear code verifier
      sessionStorage.removeItem('google_contacts_oauth_code_verifier');

      return tokens.access_token;
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      throw error;
    }
  }

  /**
   * Fetch user's contacts from Google People API
   */
  async getContacts() {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Ensure user is authenticated and get access token
      let accessToken = null;
      if (this.gapi?.client?.getToken()) {
        accessToken = this.gapi.client.getToken().access_token;
      } else {
        accessToken = await this.authenticate();
        // Store the token in gapi client for consistency
        if (this.gapi?.client) {
          this.gapi.client.setToken({ access_token: accessToken });
        }
      }

      // Use direct REST API call instead of gapi.client to avoid discovery document issues
      const response = await fetch(
        'https://people.googleapis.com/v1/people/me/connections?' +
        'personFields=names,emailAddresses,phoneNumbers,photos&' +
        'pageSize=100&' +
        'sortOrder=LAST_MODIFIED_DESCENDING',
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const connections = data.connections || [];
      
      // Transform the data into a more usable format
      const contacts = connections
        .filter(person => {
          // Only include contacts with names and either email or phone
          const hasName = person.names && person.names.length > 0;
          const hasEmail = person.emailAddresses && person.emailAddresses.length > 0;
          const hasPhone = person.phoneNumbers && person.phoneNumbers.length > 0;
          return hasName && (hasEmail || hasPhone);
        })
        .map(person => {
          const name = person.names[0]?.displayName || person.names[0]?.givenName || 'Unknown';
          const email = person.emailAddresses?.[0]?.value || null;
          const phone = person.phoneNumbers?.[0]?.value || null;
          const photo = person.photos?.[0]?.url || null;

          return {
            id: person.resourceName,
            name,
            email,
            phone,
            photo,
            displayText: email || phone || 'No contact info'
          };
        });

      return contacts;

    } catch (error) {
      console.error('Error fetching contacts:', error);
      
      // Check for specific error types
      if (error.status === 403 || error.message?.includes('403')) {
        throw new Error('Access denied to Google Contacts. Please make sure the Google People API is enabled in your Google Cloud Console and you have the necessary permissions.');
      }
      
      if (error.status === 401 || error.message?.includes('401')) {
        throw new Error('Authentication failed. Please try signing in again.');
      }
      
      throw new Error(`Failed to fetch contacts from Google: ${error.message || error}`);
    }
  }

  /**
   * Sign out user and revoke access
   */
  async signOut() {
    try {
      if (this.gapi?.client?.getToken()) {
        await this.gapi.client.getToken().access_token && 
              window.google.accounts.oauth2.revoke(this.gapi.client.getToken().access_token);
        this.gapi.client.setToken('');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated() {
    return this.gapi?.client?.getToken() !== null && this.gapi?.client?.getToken() !== undefined;
  }

  /**
   * Get current user info (if available)
   */
  async getCurrentUser() {
    try {
      if (!this.isAuthenticated()) {
        return null;
      }

      // Get access token
      let accessToken = null;
      if (this.gapi?.client?.getToken()) {
        accessToken = this.gapi.client.getToken().access_token;
      } else {
        return null;
      }

      // Use direct REST API call
      const response = await fetch(
        'https://people.googleapis.com/v1/people/me?personFields=names,emailAddresses,photos',
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const person = data;
      return {
        name: person.names?.[0]?.displayName || 'Unknown User',
        email: person.emailAddresses?.[0]?.value || null,
        photo: person.photos?.[0]?.url || null
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}

// Export singleton instance
export default new GoogleContactsService();
